import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ContactFormService } from './contact-form.service';

@Component({
  selector: 'app-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.css']
})
export class ContactFormComponent implements OnInit {
  private _visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() 
  get visible(): boolean {
    return this._visible;
  }
  
  set visible(value: boolean) {
    this._visible = value;
    if (value && !this.isLoadingUserInfo) {
      this.loadUserInfo();
    }
  }

  contactForm: FormGroup;
  isSubmitting = false;
  isLoadingUserInfo = false;

  constructor(
    private fb: FormBuilder,
    private contactFormService: ContactFormService,
    private messageService: MessageService
  ) {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, this.xssValidator]],
      email: ['', [Validators.required, this.strictEmailValidator]],
      subject: ['', [Validators.required, this.xssValidator]],
      message: ['', [Validators.required, this.xssValidator]]
    });
  }

  private strictEmailValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.value;
    if (!email) return null;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) ? null : { strictEmail: true };
  }

  private xssValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    
    const dangerousPatterns = /<script|javascript:|on\w+\s*=|<iframe|<object|<embed/i;
    return dangerousPatterns.test(value) ? { xss: true } : null;
  }

  ngOnInit() {
    // User info will be loaded when modal opens
  }

  private async loadUserInfo() {
    try {
      this.isLoadingUserInfo = true;
      const userInfo = await this.contactFormService.getUserInfo();
      
      this.contactForm.patchValue({
        name: `${userInfo.firstName} ${userInfo.lastName}`.trim(),
        email: userInfo.email
      });
    } catch (error) {
      // Silently fail - user can still fill the form manually
      console.warn('Could not load user info for contact form:', error);
    } finally {
      this.isLoadingUserInfo = false;
    }
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.contactForm.reset();
  }

  async onSubmit() {
    if (this.contactForm.valid) {
      this.isSubmitting = true;
      
      try {
        await this.contactFormService.submitContactForm(this.contactForm.value);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Your message has been sent successfully. We will get back to you soon.'
        });
        
        this.onClose();
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to send message. Please try again later.'
        });
      } finally {
        this.isSubmitting = false;
      }
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.contactForm.controls).forEach(key => {
        this.contactForm.get(key)?.markAsTouched();
      });
    }
  }
}